import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Paper,
  Divider,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import { InvestorOpportunitySummaryDTO, PsiService } from '@inversor-hub/types';
import { formatCurrency, formatPercentage } from '@/utils/misc';
import { investmentTypeLabels } from '@/features/investments/constants.ts';
import { InterestProcessInfo } from '@/features/opportunities/InterestProcessInfo.tsx';
import { PsiServiceSelection } from '@/features/opportunities/components/PsiServiceSelection';
import { useGetPsiProfile } from '@/lib/api/psis';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EuroIcon from '@mui/icons-material/Euro';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HomeWorkIcon from '@mui/icons-material/HomeWork';

interface InterestConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedServices: PsiService[]) => void;
  opportunity: InvestorOpportunitySummaryDTO;
  loading?: boolean;
}

// Helper to get color and label based on matching score
const getMatchingConfig = (score: 'HIGH' | 'MEDIUM' | 'LOW') => {
  switch (score) {
    case 'HIGH':
      return { color: 'success' as const, label: 'Alta compatibilidad' };
    case 'MEDIUM':
      return { color: 'warning' as const, label: 'Compatibilidad media' };
    case 'LOW':
      return { color: 'error' as const, label: 'Baja compatibilidad' };
  }
};

export const InterestConfirmationModal: React.FC<InterestConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  opportunity,
  loading = false,
}) => {
  const theme = useTheme();
  const [selectedServices, setSelectedServices] = useState<PsiService[]>(['OPPORTUNITY_SEARCH']);
  const { data: psiProfile, isLoading: isLoadingPsi } = useGetPsiProfile(opportunity.psi.id);

  const handleServiceToggle = (service: PsiService) => {
    setSelectedServices((prev) =>
      prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedServices);
  };

  const isConfirmDisabled = selectedServices.length === 0 || loading || isLoadingPsi;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack spacing={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleOutlineIcon color="primary" />
            <Typography variant="h6">Confirmar interés</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Estás a punto de mostrar tu interés en esta oportunidad
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Opportunity Preview Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              borderRadius: 2,
            }}
          >
            <Stack spacing={2}>
              {/* Title */}
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {opportunity.title}
                </Typography>
              </Box>

              <Divider />

              {/* Key Details Grid */}
              <Stack spacing={1.5}>
                {/* Investment Type */}
                <Box display="flex" alignItems="center" gap={1.5}>
                  <HomeWorkIcon sx={{ color: 'info.main', fontSize: 20 }} />
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                      Tipo:
                    </Typography>
                    <Chip
                      label={investmentTypeLabels[opportunity.type]}
                      size="small"
                      color="info"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" gap={1.5}>
                  <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    {opportunity.city ?? opportunity.province}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1.5}>
                  <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                  <Typography variant="body2" fontWeight={600}>
                    {formatPercentage(opportunity.grossReturn)} Rentabilidad bruta
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1.5}>
                  <EuroIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">
                    {formatCurrency(opportunity.negotiatedPrice, 0)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1.5}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">
                    PSI: <strong>{opportunity.psi.name}</strong>
                  </Typography>
                </Box>
              </Stack>

              {/* Matching Score */}
              {opportunity.matching && (
                <>
                  <Divider />
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(
                        theme.palette[getMatchingConfig(opportunity.matching.score).color].main,
                        0.08
                      ),
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Compatibilidad con tu perfil
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={getMatchingConfig(opportunity.matching.score).label}
                        size="small"
                        color={getMatchingConfig(opportunity.matching.score).color}
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </Stack>
          </Paper>

          <PsiServiceSelection
            psiProfile={psiProfile}
            isLoading={isLoadingPsi}
            selectedServices={selectedServices}
            onServiceToggle={handleServiceToggle}
            disabled={loading}
            showError={selectedServices.length === 0}
          />

          <InterestProcessInfo onClose={onClose} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={isConfirmDisabled}
          autoFocus
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
          }}
        >
          {loading ? 'Enviando...' : 'Confirmar interés'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
